"use client";

import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    getPaginationRowModel,
    useReactTable,
    getSortedRowModel,
    SortingState,
    VisibilityState,
} from "@tanstack/react-table";

import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { DataTablePagination } from "./TablePagination";
import { useTableStore } from "@/zustand-hooks/table";
import Spinner from "../ui/spinner";
import { QueryObserverResult, RefetchOptions } from "@tanstack/react-query";
import { Check, RefreshCcw, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Checkbox } from "../ui/checkbox";
import { useTranslations } from "next-intl";

interface DataTableProps<TData, TValue> {
    columns: ColumnDef<TData, TValue>[];
    data: TData[];
    isLoading: boolean;
    isPaginated?: boolean;
    tableName: string;
    currentPage?: number;
    setCurrentPage: Dispatch<SetStateAction<number>>;
    totalPages?: number;
    setPerPage: Dispatch<SetStateAction<number>>;
    perPage?: number;
    Actions?: React.ReactNode;
    cellClassName?: string;
    rowClassName?: string;
    tbodyClassName?: string;
    tableClassName?: string;
    theadClassName?: string;
    refetch?: (
        options?: RefetchOptions
    ) => Promise<QueryObserverResult<any, Error>>;
}
export function DataTable<TData, TValue>({
    columns,
    cellClassName,
    rowClassName,
    tbodyClassName,
    theadClassName,
    isPaginated = true,
    tableClassName,
    data,
    currentPage = 1,
    perPage = 5,
    tableName,
    Actions,
    setPerPage,
    refetch,
    setCurrentPage,
    isLoading,
    totalPages = 1,
}: DataTableProps<TData, TValue>) {
    const table_elements = useTranslations('table_elements');
    const storedIsBordered = JSON.parse(
        localStorage.getItem(`bordered-${tableName}`) as string
    );

    const [sorting, setSorting] = useState<SortingState>([]);
    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
    const [rowSelection, setRowSelection] = useState({});
    const [refetchDelay, setRefetchDelay] = useState(false);
    const [isBordered, setIsBordered] = useState(storedIsBordered || false);
    const { selectTableRows, items } = useTableStore();

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        onSortingChange: setSorting,
        getSortedRowModel: getSortedRowModel(),
        onColumnVisibilityChange: setColumnVisibility,
        onRowSelectionChange: setRowSelection,
        initialState: {
            pagination: {
                pageSize: perPage,
            },
        },
        state: {
            rowSelection,
            sorting,
            columnVisibility,
        },
    });



    return (
        <div className="flex font-sans max-w-screen justify-center">
            <div className="w-full overflow-hidden">
                <div className="flex items-center justify-between space-x-2 ">
                    {" "}
                    <div className="flex items-center mb-4 gap-4 ">
                        {" "}
                        {refetch && (
                            <Button
                                disabled={refetchDelay}
                                onClick={() => {
                                    if (!refetchDelay) {
                                        refetch();
                                        setRefetchDelay(true);
                                    }
                                    setTimeout(() => setRefetchDelay(false), 2000);
                                }}
                            >
                                <RefreshCcw />
                            </Button>
                        )}
                    </div>

                </div>
                <div className="rounded-md border-2 border-surface-border bg-surface overflow-hidden">
                    <Table className={cn("font-sans", tableClassName)}>
                        <TableHeader className={cn("sticky top-0 bg-surface")}>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <TableRow key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => {
                                        return (
                                            <TableHead
                                                className={cn("text-nowrap", theadClassName)}
                                                key={header.id}
                                            >
                                                {header.isPlaceholder
                                                    ? null
                                                    : flexRender(
                                                        header.column.columnDef.header,
                                                        header.getContext()
                                                    )}
                                            </TableHead>
                                        );
                                    })}
                                </TableRow>
                            ))}
                        </TableHeader>
                        <TableBody className={cn("font-normal", tbodyClassName)}>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={columns.length}
                                        className="h-24 text-center"
                                    >
                                        <div className="flex justify-center">
                                            <Spinner size={24} />
                                        </div>{" "}
                                    </TableCell>
                                </TableRow>
                            ) : table.getRowModel().rows?.length ? (
                                table.getRowModel().rows.map((row) => (
                                    <TableRow
                                        className={cn("", rowClassName)}
                                        key={row.id}
                                        data-state={row.getIsSelected() && "selected"}
                                    >
                                        {row.getVisibleCells().map((cell) => (
                                            <TableCell
                                                className={cn(
                                                    "px-2 text-nowrap",
                                                    cellClassName,
                                                    isBordered ? "border-2" : ""
                                                )}
                                                key={cell.id}
                                            >
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext()
                                                )}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell
                                        colSpan={columns.length}
                                        className="h-24 text-center"
                                    >
                                        {table_elements('no_data')}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>
                {isPaginated && (
                    <div className="flex items-center justify-end space-x-2 py-4">
                        <DataTablePagination
                            totalPages={totalPages}
                            currentPage={currentPage}
                            setCurrentPage={setCurrentPage}
                            tableName={tableName}
                            table={table}
                            perPage={perPage}
                            setPerPage={setPerPage}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
